import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BASE_URI } from './globals';
import { useRegionContext } from './context/RegionContext';

export const useFetchGet = (defaultUrl, initialData) => {
    const [url, setUrl] = useState(defaultUrl);
    const [data, setData] = useState(initialData);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        const inner = async () => {
            setIsPending(true);
            const res = await fetch(url, { signal: controller.signal });
            if (res.ok) {
                const newData = await res.json();
                setIsPending(false);
                setData(newData);
                setError(null);
            } else {
                setIsPending(false);
                setData(null);
                setError({
                    status: res.status,
                    message: res.statusText
                });
            }
        };

        if (url) {
            inner();
        }

        return () => {
            controller.abort();
        };
    }, [url]);

    const clear = () => {
        setData(initialData);
        setError(null);
    };

    return {
        data,
        setData,
        isPending,
        error,
        setUrl,
        clear
    };
};

export const useFetchGetRef = (defaultUrl, initialData) => {
    const [refUrl, setRefUrl] = useState(defaultUrl);
    const [refData, setRefData] = useState(initialData);
    const [isPendingRef, setIsPendingRef] = useState(false);
    const [refError, setRefError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        const inner = async () => {
            setIsPendingRef(true);
            const res = await fetch(refUrl, { signal: controller.signal });
            if (res.ok) {
                const newRefData = await res.json();
                setRefData(newRefData);
                setIsPendingRef(false);
            } else {
                setRefData(null);
                setIsPendingRef(false);
                setRefError({
                    status: res.status,
                    message: res.statusText
                });
            }
        };

        if (refUrl) {
            inner();
        }

        return () => {
            controller.abort();
        };
    }, [refUrl]);

    const clearRef = () => {
        setRefData(initialData);
    };

    return {
        refData,
        setRefData,
        isPendingRef,
        setRefUrl,
        clearRef,
        refError
    };
};

export const useFetchPostOrPut = (initialData, method = 'POST') => {
    const [data, setData] = useState(initialData);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(null);
    const [numberOfRunningReq, setNumberOfRunningReq] = useState(0);

    useEffect(() => {
        setIsPending(numberOfRunningReq > 0);
    }, [numberOfRunningReq]);

    const send = async (url, body) => {
        setNumberOfRunningReq(previous => previous + 1);
        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        if (res.ok) {
            const newData = await res.json();
            setNumberOfRunningReq(previous => previous - 1);
            setData(newData);
            setError(null);

            return data;
        }
        setNumberOfRunningReq(previous => previous - 1);
        setData(null);
        setError({
            status: res.status,
            message: res.statusText
        });

        return null;
    };

    const clear = () => {
        setData(initialData);
        setError(null);
    };

    return {
        send,
        data,
        setData,
        isPending,
        error,
        clear
    };
};

export const useDelayedState = (initialValue, delay = 500) => {
    const [directState, setDirectState] = useState(initialValue);
    const [delayedState, setDelayedState] = useState(initialValue);

    useEffect(() => {
        const delayQuery = setTimeout(() => {
            setDelayedState(directState);
        }, delay);

        return () => clearTimeout(delayQuery);
    }, [directState, delay]);

    const setDelayedStateDirectly = val => {
        setDelayedState(val);
        setDirectState(val);
    };

    return [delayedState, setDirectState, directState, setDelayedStateDirectly];
};

export function useSearchParamsState(searchParamName, defaultValue) {
    const [searchParams, setSearchParams] = useSearchParams();

    const acquiredSearchParam = searchParams.get(searchParamName);
    const searchParamsState = acquiredSearchParam ?? defaultValue;

    const setSearchParamsState = newState => {
        const next = {
            ...[...searchParams.entries()].reduce(
                (o, [key, value]) => ({ ...o, [key]: value }),
                {}
            ),
            [searchParamName]: newState
        };
        setSearchParams(next);
    };
    return [searchParamsState, setSearchParamsState];
}

export const useDomainRegion = () => {
    const { region, setRegion, clearRegion, setRegionData } = useRegionContext();
    const [status, setStatus] = useState('fetching');

    useEffect(() => {
        const firstPath = window.location.hostname.split('.')[0];
        const newRegion = firstPath === 'www' ? window.location.hostname.split('.')[1] : firstPath;
        setRegion(newRegion);

        return () => {
            clearRegion();
        };
    }, [clearRegion, setRegion]);

    const {
        refData: regionData,
        setRefUrl: setRegionDataUrl,
        refError
    } = useFetchGetRef(null, null);
    useEffect(() => {
        if (region) {
            setRegionDataUrl(`${BASE_URI}/region/${region}/`);
        }
    }, [region, setRegionDataUrl]);

    useEffect(() => {
        if (regionData) {
            setRegionData(regionData);
            setStatus('success');
        } else if (refError) {
            setStatus('error');
        }
    }, [regionData, refError, setRegionData]);

    return { region, status, regionData };
};
